import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Link from 'next/link';
import { signOut, useSession } from 'next-auth/react';
import React from 'react';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ProfileModal: React.FC<ProfileModalProps> = ({ isOpen, onClose }) => {
  const session = useSession();

  // Prevent background scrolling when modal is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [isOpen]);

  // Handle closing the modal when pressing the Escape key
  React.useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleEsc);
    } else {
      window.removeEventListener('keydown', handleEsc);
    }

    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Modal */}
      <div
        className={`fixed right-0 top-0 z-50 h-full bg-white shadow-lg transition-transform duration-300 dark:bg-gray-800 ${
          isOpen ? 'w-[85vw] translate-x-0 md:w-[85vw]' : 'w-0 translate-x-full'
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-modal-title"
      >
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b p-4 ">
            <h2
              id="profile-modal-title"
              className="text-lg font-medium text-gray-900 "
            >
              會員中心
            </h2>
            <button
              type="button"
              className="text-black hover:text-gray-500 "
              onClick={onClose}
              aria-label="Close panel"
            >
              {/* Close Icon */}
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="mb-6 flex items-center space-x-3">
              <AccountCircleIcon className="text-4xl text-gray-500 " />
              <div>
                <div className="text-xl font-semibold text-gray-900 ">
                  {session?.data?.user?.id || '會員'}
                </div>
                <div className="text-sm text-gray-500 ">
                  {session?.data?.user?.email}
                </div>
              </div>
            </div>

            <ul className="space-y-2">
              <li>
                <Link href="/dashboard">
                  <span
                    className="block cursor-pointer rounded px-4 py-2 text-black hover:bg-gray-100  "
                    onClick={onClose}
                  >
                    會員資料
                  </span>
                </Link>
              </li>
              <li>
                <Link href="/dashboard/upload-license-plate">
                  <span
                    className="block cursor-pointer rounded px-4 py-2 text-black hover:bg-gray-100  "
                    onClick={onClose}
                  >
                    上架車牌
                  </span>
                </Link>
              </li>
              <li>
                <Link href="/dashboard/upload-car">
                  <span
                    className="block cursor-pointer rounded px-4 py-2 text-black hover:bg-gray-100  "
                    onClick={onClose}
                  >
                    上架二手車
                  </span>
                </Link>
              </li>
              <li>
                <Link href="/dashboard/user-profile">
                  <span
                    className="block cursor-pointer rounded px-4 py-2 text-black hover:bg-gray-100  "
                    onClick={onClose}
                  >
                    會員設定
                  </span>
                </Link>
              </li>
            </ul>

            <div className="mt-6">
              <button
                type="button"
                onClick={() => {
                  signOut();
                  onClose();
                }}
                className="w-full rounded px-4 py-2 text-left text-black hover:bg-gray-100  "
              >
                登出
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProfileModal;
